import {
  createAsyncThunk,
  createSlice,
  PayloadAction,
  SerializedError,
} from '@reduxjs/toolkit'
import {
  createBooking as createBookingAsync,
  CreateBookingResponse,
  createProceedBooking,
  DeviceMeta,
  getBookingFlowType,
  GetBookingFlowTypeResponse,
  Metadata,
  rescheduleExternalBooking as rescheduleExternalBookingAPI,
  RescheduleExternalBookingResponse,
  Screens,
} from 'patient-api-lib'
import Personnummer from 'personnummer'
import { DataLayerEventName } from '../../models/enums'
import { AppDispatch, RootState } from '../../store'
import { blockFingerprint } from '../../util/dataLayerEvents'
import { joinTextMessage } from '../../util/joinTextMessage'
import {
  BookingFailedData,
  sendBookingContactInfo,
  sendBookingFailed,
  sendRescheduleComplete,
  sendStartNewBooking,
  sendStartReschedule,
} from '../../util/messageHandler'
import { ContactInformation } from '../contactInformationScreenSlice'
import { navigateBackTo } from '../screensRouterSlice'
import { determineErrorType } from '../../util/errorTypeHandler'
import { getSelectedTreatmentObject } from '../treatmentScreen'
import { TokenizationSession } from '../../models/types'
import { getValidPromoCodeValue } from '../promoCode/promoCodeSlice'

export type BookingFlowState = {
  success: boolean
  error: boolean
  tokenizationSession: TokenizationSession | null
  noShowFee: number | undefined
  errorObject: SerializedError | null
  pending: boolean
  isRebooking: boolean
  bookingId: string | undefined
}

export const initialState: BookingFlowState = {
  success: false,
  error: false,
  tokenizationSession: null,
  errorObject: null,
  pending: false,
  isRebooking: false,
  bookingId: undefined,
  noShowFee: undefined,
}

export const done = createAsyncThunk<Screens, Screens>(
  'bookingFlow/done',
  async (screen) => {
    return screen
  }
)

export const rescheduleExternalBooking = createAsyncThunk<
  RescheduleExternalBookingResponse.AsObject,
  void,
  { dispatch: AppDispatch; state: RootState }
>('bookingFlow/rescheduleExternalBooking', async (_, thunkApi) => {
  const state = thunkApi.getState() as RootState
  const token = state.myBookingsScreen.jwt
  const connectionId = state.yourBookingsScreen.selectedBooking?.connectionId
  const externalBookingId = state.yourBookingsScreen.selectedBooking?.id
  const clinicId = state.yourBookingsScreen.selectedBooking?.clinicId
  const appointmentId = state.appointmentScreen.selectedAppointment?.id
  const selectedClinic = state.clinicScreen.selectedClinic
  const selectedAppointment = state.appointmentScreen.selectedAppointment
  const selectedTreatment = state.treatmentScreen.selectedTreatment
  const selectedTreatmentObject = getSelectedTreatmentObject(
    selectedTreatment,
    selectedAppointment
  )
  thunkApi.dispatch(setSuccessState({ success: false, error: false }))
  const missingFields: string[] = []

  try {
    if (
      !token ||
      !connectionId ||
      !externalBookingId ||
      !clinicId ||
      !appointmentId
    ) {
      thunkApi.dispatch(setSuccessState({ success: false, error: true }))
      if (!token) missingFields.push('token')
      if (!connectionId) missingFields.push('connectionId')
      if (!externalBookingId) missingFields.push('externalBookingId')
      if (!clinicId) missingFields.push('clinicId')
      if (!appointmentId) missingFields.push('appointmentId')

      return thunkApi.rejectWithValue(
        `Missing required fields: ${missingFields.join(', ')}`
      )
    }

    sendStartReschedule(
      connectionId,
      externalBookingId,
      clinicId,
      appointmentId
    )

    const res = await rescheduleExternalBookingAPI(
      token,
      connectionId,
      externalBookingId,
      clinicId,
      appointmentId
    )

    thunkApi.dispatch(
      setSuccessState({ success: res.hasSuccess(), error: res.hasFailure() })
    )

    if (res.hasFailure()) {
      const preconditionError = res.getFailure()?.getPreconditionError()
      const integrationError = res.getFailure()?.getIntegrationError()
      const errorType = determineErrorType(preconditionError, integrationError)

      if (!errorType) {
        return thunkApi.rejectWithValue('Error type is undefined')
      }

      const eventCompletePayloadFacebook: BookingFailedData = {
        clinicName: selectedClinic?.name || '',
        appointmentID: selectedAppointment?.id || '',
        appointmentName: selectedTreatmentObject?.name || '',
        connectionId: connectionId,
        externalBookingId: externalBookingId,
        errorType,
      }

      sendBookingFailed(eventCompletePayloadFacebook)

      return thunkApi.rejectWithValue('Failed to reschedule booking')
    }

    thunkApi.dispatch(done(Screens.BookingConfirmationScreen))

    if (res?.hasSuccess()) {
      const success = res?.getSuccess()
      const responseClinicId =
        success?.getExternalBooking()?.getClinicId() || ''
      const responseTreatmentId =
        success?.getExternalBooking()?.getTreatmentId()?.getValue() || ''
      const responseCaregiverName =
        success?.getExternalBooking()?.getCaregiverName()?.getValue() || ''
      const responseExternalBookingId =
        success?.getExternalBooking()?.getId() || ''
      const responseConnectionId =
        success?.getExternalBooking()?.getConnectionId() || ''

      sendRescheduleComplete(
        responseClinicId,
        responseConnectionId,
        responseTreatmentId,
        responseCaregiverName,
        responseExternalBookingId,
        appointmentId
      )
    }

    return res.toObject()
  } catch (error: unknown) {
    const err = error as Error
    thunkApi.dispatch(setSuccessState({ success: false, error: true }))

    const eventCompletePayloadFacebook: BookingFailedData = {
      clinicName: selectedClinic?.name || '',
      appointmentID: selectedAppointment?.id || '',
      appointmentName: selectedTreatmentObject?.name || '',
      connectionId: connectionId,
      externalBookingId: externalBookingId,
      errorType: err.cause,
    }

    sendBookingFailed(eventCompletePayloadFacebook)

    return thunkApi.rejectWithValue(err.message)
  }
})

export const createBooking = createAsyncThunk<
  CreateBookingResponse.AsObject,
  Metadata,
  { dispatch: AppDispatch; state: RootState }
>('bookingFlow/createBooking', async (metaData, thunkApi) => {
  const state = thunkApi.getState()
  const appointmentId = state.appointmentScreen.selectedAppointment?.id
  const clinicId = state.clinicScreen.selectedClinic?.id
  const selectedClinic = state.clinicScreen.selectedClinic
  const selectedAppointment = state.appointmentScreen.selectedAppointment
  const selectedTreatment = state.treatmentScreen.selectedTreatment
  const selectedTreatmentObject = getSelectedTreatmentObject(
    selectedTreatment,
    selectedAppointment
  )
  const promoCode = getValidPromoCodeValue(state)

  const {
    fName,
    ssn,
    lName,
    phone,
    email,
    msgToClinic,
    scaredOfDentist,
    termsAndConditions,
  } = state.contactInformationScreen
  const missingFields: string[] = []

  thunkApi.dispatch(setSuccessState({ success: false, error: false }))

  try {
    const message = joinTextMessage(msgToClinic)

    thunkApi.dispatch(setSuccessState({ success: false, error: false }))

    if (
      !clinicId ||
      !appointmentId ||
      !fName ||
      !lName ||
      !ssn ||
      !phone ||
      !email
    ) {
      thunkApi.dispatch(setSuccessState({ success: false, error: true }))
      if (!clinicId) missingFields.push('clinicId')
      if (!appointmentId) missingFields.push('appointmentId')
      if (!fName) missingFields.push('fName')
      if (!lName) missingFields.push('lName')
      if (!ssn) missingFields.push('ssn')
      if (!phone) missingFields.push('phone')
      if (!email) missingFields.push('email')

      return thunkApi.rejectWithValue(
        `Missing required fields: ${missingFields.join(', ')}`
      )
    }

    const contactInformationPayload: ContactInformation = {
      fName,
      lName,
      phone: '',
      email,
      ssn: '',
      termsAndConditions,
      scaredOfDentist,
      msgToClinic,
    }

    sendStartNewBooking(contactInformationPayload, appointmentId, promoCode)

    const res = await createBookingAsync(
      clinicId,
      appointmentId,
      fName,
      lName,
      email,
      phone,
      +Personnummer.parse(ssn).format(true),
      message,
      metaData,
      promoCode
    )

    thunkApi.dispatch(
      setSuccessState({ success: res.hasSuccess(), error: res.hasFailure() })
    )

    if (res.hasFailure()) {
      const preconditionError = res.getFailure()?.getPreconditionError()
      const integrationError = res.getFailure()?.getIntegrationError()
      const errorType = determineErrorType(preconditionError, integrationError)

      if (!errorType) {
        return thunkApi.rejectWithValue('Error type is undefined')
      }

      const eventCompletePayloadFacebook: BookingFailedData = {
        clinicName: selectedClinic?.name || '',
        appointmentID: selectedAppointment?.id || '',
        appointmentName: selectedTreatmentObject?.name || '',
        emailAddress: email || '',
        phoneNumber: phone || '',
        firstName: fName || '',
        lastName: lName || '',
        errorType,
      }

      sendBookingFailed(eventCompletePayloadFacebook)

      return thunkApi.rejectWithValue(res.getFailure()?.getMessage())
    }

    const bookingActions = res.getPendingActions()?.getBookingActionsList()
    const isAdyenTokenizationRequired = bookingActions?.includes(
      CreateBookingResponse.BookingAction.BOOKING_ACTION_ADYEN_CARD_TOKENIZATION
    )

    const bookingId =
      res.getSuccess()?.getBookingId() ||
      res.getPendingActions()?.getBookingId()

    if (!bookingId) {
      thunkApi.dispatch(setSuccessState({ success: false, error: true }))
      return thunkApi.rejectWithValue('Booking id required for booking')
    }

    if (metaData.type === DeviceMeta.Type.TYPE_UNSPECIFIED) {
      blockFingerprint({
        event: DataLayerEventName.BlockFingerprint,
        bookingId: bookingId,
      })
    }

    sendBookingContactInfo(contactInformationPayload, bookingId, promoCode)

    if (isAdyenTokenizationRequired) {
      const proceedBooking = await createProceedBooking(bookingId, {
        adyen_card_tokenization: 'https://www.google.com',
      })

      const sessionData = proceedBooking.getAdyenSessionData()?.getSessionData()
      const sessionId = proceedBooking.getAdyenSessionData()?.getSessionsId()

      if (!sessionData || !sessionId) {
        return thunkApi.rejectWithValue(
          'Session data and session id required for booking'
        )
      }

      thunkApi.dispatch(
        setTokenizationSession({
          sessionId: sessionId,
          sessionData: sessionData,
        })
      )

      thunkApi.dispatch(done(Screens.TokenizationScreen))
    } else {
      thunkApi.dispatch(done(Screens.BookingConfirmationScreen))
    }

    return res.toObject()
  } catch (error: unknown) {
    const err = error as Error
    thunkApi.dispatch(setSuccessState({ success: false, error: true }))

    const eventCompletePayloadFacebook: BookingFailedData = {
      clinicName: selectedClinic?.name || '',
      appointmentID: selectedAppointment?.id || '',
      appointmentName: selectedTreatmentObject?.name || '',
      emailAddress: email || '',
      phoneNumber: phone || '',
      firstName: fName || '',
      lastName: lName || '',
      errorType: err.cause,
    }

    sendBookingFailed(eventCompletePayloadFacebook)

    return thunkApi.rejectWithValue(err.message)
  }
})

export const fetchBookingFlowType = createAsyncThunk<
  GetBookingFlowTypeResponse.BookingFlowType,
  void,
  { dispatch: AppDispatch; state: RootState }
>('bookingFlow/getBookingFlowType', async (_, thunkApi) => {
  const state = thunkApi.getState()
  const appointmentId = state.appointmentScreen.selectedAppointment?.id
  const clinicId = state.clinicScreen.selectedClinic?.id
  const missingFields: string[] = []

  if (!clinicId || !appointmentId) {
    thunkApi.dispatch(setSuccessState({ success: false, error: true }))
    if (!clinicId) missingFields.push('clinicId')
    if (!appointmentId) missingFields.push('appointmentId')

    return thunkApi.rejectWithValue(
      `Missing required fields: ${missingFields.join(', ')}`
    )
  }

  const res = await getBookingFlowType(clinicId, appointmentId)
  const noShowFee = res.getBookingNoshowFee()?.getValue()
  thunkApi.dispatch(setNoShowFee(noShowFee))
  return res.getBookingFlowType()
})

export const BookingFlowSlice = createSlice({
  name: 'bookingFlowState',
  initialState,
  reducers: {
    setSuccessState: (
      state,
      action: PayloadAction<{
        success: boolean
        error: boolean
      }>
    ) => {
      state.success = action.payload.success
      state.error = action.payload.error
    },
    setIsRebooking: (state, action: PayloadAction<boolean>) => {
      state.isRebooking = action.payload
    },
    setPending: (state, action: PayloadAction<boolean>) => {
      state.pending = action.payload
    },
    setTokenizationSession: (
      state,
      action: PayloadAction<{ sessionId: string; sessionData: string } | null>
    ) => {
      state.tokenizationSession = action.payload
    },
    setNoShowFee: (state, action: PayloadAction<number | undefined>) => {
      state.noShowFee = action.payload
    },
    clearState: () => initialState,
  },
  extraReducers: (builder) => {
    builder.addCase(createBooking.pending, (state) => {
      state.pending = true
    })
    builder.addCase(createBooking.fulfilled, (state, action) => {
      state.pending = false
      state.bookingId =
        action.payload.pendingActions?.bookingId ||
        action.payload.success?.bookingId
    })
    builder.addCase(createBooking.rejected, (state, action) => {
      state.pending = false
      state.errorObject = action.error
    })
    builder.addCase(rescheduleExternalBooking.pending, (state) => {
      state.pending = true
    })
    builder.addCase(rescheduleExternalBooking.fulfilled, (state) => {
      state.pending = false
    })
    builder.addCase(rescheduleExternalBooking.rejected, (state, action) => {
      state.pending = false
      state.errorObject = action.error

      if (action.error.message === 'Unauthenticated') {
        navigateBackTo(Screens.MyBookingsScreen)
      }
    })
    builder.addCase(fetchBookingFlowType.pending, (state) => {
      state.pending = true
    })
    builder.addCase(fetchBookingFlowType.fulfilled, (state) => {
      state.pending = false
    })
    builder.addCase(fetchBookingFlowType.rejected, (state, action) => {
      state.pending = false
      state.errorObject = action.error
    })
  },
})

export const getSuccessState = (state: RootState) => ({
  success: state.bookingFlowState.success,
  error: state.bookingFlowState.error,
  errorObject: state.bookingFlowState.errorObject,
})

export const getBookingStatus = (state: RootState) =>
  state.bookingFlowState.pending

export const isRebooking = (state: RootState) =>
  state.bookingFlowState.isRebooking

export const getTokenizationSession = (state: RootState) =>
  state.bookingFlowState.tokenizationSession

export const getBookingId = (state: RootState) =>
  state.bookingFlowState.bookingId

export const getNoShowFee = (state: RootState) =>
  state.bookingFlowState.noShowFee

export const {
  setSuccessState,
  setIsRebooking,
  setPending,
  setTokenizationSession,
  setNoShowFee,
  clearState,
} = BookingFlowSlice.actions

export const bookingFlowReducer = BookingFlowSlice.reducer
